<template>
  <div class="pitch-scheduler">
    <!-- Toolbar -->
    <div class="ps-toolbar">
      <div class="ps-date-nav">
        <button class="ps-nav-btn" @click="changeDay(-1)" title="Previous day">
          <i class="bi bi-chevron-left"></i>
        </button>
        <button class="ps-today-btn" @click="goToday">Today</button>
        <button class="ps-nav-btn" @click="changeDay(1)" title="Next day">
          <i class="bi bi-chevron-right"></i>
        </button>
        <input type="date" class="ps-date-input" v-model="selectedDateStr" />
        <span class="ps-date-label">{{ prettyDate }}</span>
      </div>

      <div class="ps-toolbar-right">
        <div class="ps-filter">
          <label>Team</label>
          <select v-model="filterTeamId">
            <option value="">All teams</option>
            <option v-for="t in clubTeams" :key="t.id" :value="String(t.id)">{{ t.teamName }}</option>
          </select>
        </div>
        <button v-if="isAdmin" class="ps-btn ps-btn-outline" @click="openPitchModal('add')">
          <i class="bi bi-plus-lg"></i> Pitch
        </button>
        <button v-if="isAdmin && pitches.length" class="ps-btn ps-btn-outline" @click="openPitchModal('edit')">
          <i class="bi bi-pencil"></i> Edit pitches
        </button>
        <button v-if="canBook && pitches.length" class="ps-btn ps-btn-primary" @click="openBookingModal('add')">
          <i class="bi bi-calendar-plus"></i> New booking
        </button>
      </div>
    </div>

    <!-- Legend -->
    <div class="ps-legend">
      <span class="ps-legend-item"><span class="ps-swatch" :style="{ background: typeColors.Training }"></span>Training</span>
      <span class="ps-legend-item"><span class="ps-swatch" :style="{ background: typeColors.Match }"></span>Match</span>
      <span class="ps-legend-item"><span class="ps-swatch" :style="{ background: typeColors.Maintenance }"></span>Maintenance</span>
      <span class="ps-legend-item"><span class="ps-swatch" :style="{ background: typeColors['External hire'] }"></span>External hire</span>
      <span class="ps-legend-item"><span class="ps-swatch ps-swatch-fixture"></span>Fixture (from schedule)</span>
    </div>

    <!-- Empty state -->
    <div v-if="pitches.length === 0" class="ps-empty">
      <i class="bi bi-geo-alt"></i>
      <p>No pitches yet.</p>
      <button v-if="isAdmin" class="ps-btn ps-btn-primary" @click="openPitchModal('add')">
        <i class="bi bi-plus-lg"></i> Add your first pitch
      </button>
    </div>

    <!-- Grid -->
    <div v-else class="ps-grid-scroll">
      <div class="ps-grid" :style="{ minWidth: nameColWidth + hours.length * colWidth + 'px' }">
        <!-- Header -->
        <div class="ps-grid-head">
          <div class="ps-corner" :style="{ width: nameColWidth + 'px' }">Pitch</div>
          <div class="ps-hours">
            <div v-for="h in hours" :key="h" class="ps-hour-label" :style="{ width: colWidth + 'px' }">
              {{ formatHour(h) }}
            </div>
          </div>
        </div>

        <!-- Rows -->
        <div v-for="pitch in pitches" :key="pitch.id" class="ps-row">
          <div class="ps-row-name" :style="{ width: nameColWidth + 'px' }">
            <span class="ps-pitch-name">{{ pitch.name }}</span>
            <span class="ps-pitch-sub">{{ pitch.pitch_size }}<span v-if="pitch.pitchType"> · {{ pitch.pitchType }}</span></span>
          </div>
          <div
            class="ps-track"
            :style="{ width: hours.length * colWidth + 'px' }"
            @click="onTrackClick($event, pitch)"
          >
            <!-- hour gridlines -->
            <div v-for="h in hours" :key="'g' + h" class="ps-cell" :style="{ width: colWidth + 'px' }"></div>

            <!-- fixtures (games) — read-only -->
            <div
              v-for="g in gamesForPitch(pitch.id)"
              :key="'game' + g.id"
              class="ps-block ps-block-fixture"
              :style="blockStyle(g._start, g._end, g._color)"
              @click.stop="showGameDetails(g)"
              :title="g.text"
            >
              <span class="ps-block-time">{{ timeLabel(g._start) }}</span>
              <span class="ps-block-title">{{ g.text }}</span>
            </div>

            <!-- bookings — editable -->
            <div
              v-for="b in bookingsForPitch(pitch.id)"
              :key="'bk' + b.id"
              class="ps-block ps-block-booking"
              :style="blockStyle(b._start, b._end, typeColors[b.booking_type] || typeColors.Other)"
              @click.stop="canBook ? openBookingModal('edit', b) : null"
              :title="b.title"
            >
              <span class="ps-block-time">{{ timeLabel(b._start) }}–{{ timeLabel(b._end) }}</span>
              <span class="ps-block-title">{{ b.title }}<span v-if="b._zone"> · {{ b._zone }}</span></span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <p v-if="pitches.length" class="ps-hint">
      <i class="bi bi-info-circle"></i>
      Tap an empty slot on a pitch to create a booking. Fixtures shown with a striped edge come from the club schedule.
    </p>

    <!-- Booking modal -->
    <div v-if="bookingModal" class="ps-modal-overlay" @click.self="bookingModal = false">
      <div class="ps-modal">
        <div class="ps-modal-head">
          <h3>{{ bookingForm.id ? 'Edit booking' : 'New booking' }}</h3>
          <button class="ps-close" @click="bookingModal = false">&times;</button>
        </div>
        <div class="ps-modal-body">
          <div class="ps-field">
            <label>Pitch</label>
            <select v-model="bookingForm.pitch_id">
              <option v-for="p in pitches" :key="p.id" :value="String(p.id)">{{ p.name }}</option>
            </select>
          </div>
          <div class="ps-field">
            <label>Title</label>
            <input type="text" v-model="bookingForm.title" placeholder="e.g. U14s training" />
          </div>
          <div class="ps-field-row">
            <div class="ps-field">
              <label>Type</label>
              <select v-model="bookingForm.booking_type">
                <option v-for="t in bookingTypes" :key="t" :value="t">{{ t }}</option>
              </select>
            </div>
            <div class="ps-field">
              <label>Team (optional)</label>
              <select v-model="bookingForm.team_id">
                <option value="">—</option>
                <option v-for="t in clubTeams" :key="t.id" :value="String(t.id)">{{ t.teamName }}</option>
              </select>
            </div>
          </div>
          <div class="ps-field-row">
            <div class="ps-field">
              <label>Date</label>
              <input type="date" v-model="bookingForm.date" />
            </div>
            <div class="ps-field">
              <label>Start</label>
              <input type="time" v-model="bookingForm.start" step="300" />
            </div>
            <div class="ps-field">
              <label>End</label>
              <input type="time" v-model="bookingForm.end" step="300" />
            </div>
          </div>
          <!-- Pitch area (zones) — only for pitches big enough to split into thirds -->
          <div v-if="pitchSplittable" class="ps-field ps-zones">
            <label>Pitch area</label>
            <p class="ps-zones-hint">Book the full pitch, or just the thirds you need so another team can use the rest.</p>
            <label class="ps-zone-opt">
              <input type="radio" :checked="isFullPitch()" @change="setFullPitch()" />
              <span>Full pitch</span>
            </label>
            <div class="ps-zone-grid">
              <label
                v-for="n in 3"
                :key="'z' + n"
                class="ps-zone-cell"
                :class="{ on: isZoneOn(n), taken: bookedZonesForSlot.includes(String(n)) }"
              >
                <input
                  type="checkbox"
                  :checked="isZoneOn(n)"
                  :disabled="bookedZonesForSlot.includes(String(n))"
                  @change="toggleZone(n)"
                />
                <span>Zone {{ n }}</span>
                <small v-if="bookedZonesForSlot.includes(String(n))">booked</small>
              </label>
            </div>
          </div>

          <div class="ps-field">
            <label>Notes (optional)</label>
            <textarea v-model="bookingForm.notes" rows="2" placeholder="Anything worth noting"></textarea>
          </div>
          <p v-if="bookingError" class="ps-error"><i class="bi bi-exclamation-triangle"></i> {{ bookingError }}</p>
        </div>
        <div class="ps-modal-foot">
          <button v-if="bookingForm.id" class="ps-btn ps-btn-danger" @click="deleteBooking" :disabled="saving">
            <i class="bi bi-trash"></i> Delete
          </button>
          <span class="ps-spacer"></span>
          <button class="ps-btn ps-btn-outline" @click="bookingModal = false">Cancel</button>
          <button class="ps-btn ps-btn-primary" @click="saveBooking" :disabled="saving">
            {{ saving ? 'Saving…' : (bookingForm.id ? 'Save changes' : 'Create booking') }}
          </button>
        </div>
      </div>
    </div>

    <!-- Pitch add/edit modal -->
    <div v-if="pitchModal" class="ps-modal-overlay" @click.self="pitchModal = false">
      <div class="ps-modal">
        <div class="ps-modal-head">
          <h3>{{ pitchMode === 'add' ? 'Add a pitch' : 'Edit pitches' }}</h3>
          <button class="ps-close" @click="pitchModal = false">&times;</button>
        </div>
        <div class="ps-modal-body">
          <div v-if="pitchMode === 'edit'" class="ps-field">
            <label>Select pitch</label>
            <select v-model="editPitchId" @change="loadPitchIntoForm">
              <option value="">Choose a pitch…</option>
              <option v-for="p in pitches" :key="p.id" :value="String(p.id)">{{ p.name }}</option>
            </select>
          </div>
          <template v-if="pitchMode === 'add' || editPitchId">
            <div class="ps-field">
              <label>Pitch name</label>
              <input type="text" v-model="pitchForm.name" />
            </div>
            <div class="ps-field-row">
              <div class="ps-field">
                <label>Size</label>
                <select v-model="pitchForm.pitch_size">
                  <option value="">—</option>
                  <option>11-a-side</option><option>9-a-side</option>
                  <option>7-a-side</option><option>5-a-side</option><option>Other</option>
                </select>
              </div>
              <div class="ps-field">
                <label>Surface</label>
                <select v-model="pitchForm.pitchType">
                  <option value="">—</option>
                  <option>4G</option><option>3G</option><option>Grass</option><option>Other</option>
                </select>
              </div>
            </div>
            <div class="ps-field">
              <label>Address</label>
              <input type="text" v-model="pitchForm.address" />
            </div>
            <div class="ps-field">
              <label>Postcode</label>
              <input type="text" v-model="pitchForm.postCode" />
            </div>
          </template>
        </div>
        <div class="ps-modal-foot">
          <span class="ps-spacer"></span>
          <button class="ps-btn ps-btn-outline" @click="pitchModal = false">Cancel</button>
          <button class="ps-btn ps-btn-primary" @click="savePitch" :disabled="saving || (pitchMode === 'edit' && !editPitchId)">
            {{ pitchMode === 'add' ? 'Add pitch' : 'Save pitch' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Game details modal (read-only) -->
    <div v-if="gameModal" class="ps-modal-overlay" @click.self="gameModal = false">
      <div class="ps-modal">
        <div class="ps-modal-head">
          <h3>{{ selectedGame.gameType }}<span v-if="selectedGame.gameType !== 'Training' && selectedGame.opposition"> v {{ selectedGame.opposition }}</span></h3>
          <button class="ps-close" @click="gameModal = false">&times;</button>
        </div>
        <div class="ps-modal-body">
          <p><i class="bi bi-people"></i> {{ selectedGame.teamName }}</p>
          <p><i class="bi bi-clock"></i> {{ timeLabel(selectedGame._start) }}–{{ timeLabel(selectedGame._end) }}</p>
          <p v-if="selectedGame.address"><i class="bi bi-geo-alt"></i> {{ selectedGame.address }} {{ selectedGame.postCode }}</p>
          <p v-if="selectedGame.pitchType"><i class="bi bi-grid-3x3"></i> {{ selectedGame.pitchType }}</p>
          <p v-if="selectedGame.notes"><i class="bi bi-chat-left-text"></i> {{ selectedGame.notes }}</p>
          <p class="ps-muted">Fixtures are managed in the club schedule, not here.</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { CHECK_PROFILE_HAS_DATA } from "@/store/actions/user.js";
import { Games } from "@/services/games-service.js";
import { Users } from "@/services/user-service.js";
import { Teams } from "@/services/teams-service.js";
import { Pitches } from "@/services/coaches-pitches.js";
import { PitchBookings } from "@/services/pitch-bookings.js";
import removeDuplicatesMixin from "@/mixin/removeDuplicates.js";
import setPitchZoneTextMixin from "@/mixin/pitchZoneText.js";
import moment from "moment";

const DAY_START = 7; // 07:00
const DAY_END = 22; // 22:00

export default {
  name: "PitchScheduler",
  mixins: [removeDuplicatesMixin, setPitchZoneTextMixin],
  data() {
    const today = moment().format("YYYY-MM-DD");
    return {
      userData: { name: "", userType: "", clubId: "", id: "" },
      pitches: [],
      games: [],
      bookings: [],
      clubTeams: [],
      filterTeamId: "",
      selectedDateStr: today,
      colWidth: 68,
      nameColWidth: 140,
      dayStart: DAY_START,
      dayEnd: DAY_END,
      saving: false,
      bookingModal: false,
      bookingError: "",
      bookingForm: this.emptyBooking(today),
      pitchModal: false,
      pitchMode: "add",
      editPitchId: "",
      pitchForm: {},
      gameModal: false,
      selectedGame: {},
      bookingTypes: ["Training", "Match", "Friendly", "Maintenance", "External hire", "Other"],
      typeColors: {
        Training: "#1155cc",
        Match: "#019e53",
        Friendly: "#17a2b8",
        Maintenance: "#6c757d",
        "External hire": "#b8860b",
        Other: "#7c838a",
      },
    };
  },
  computed: {
    ...mapGetters(["getProfile"]),
    isAdmin() {
      return this.userData.userType === "Admin";
    },
    canBook() {
      return ["Admin", "Coach", "HeadCoach"].includes(this.userData.userType);
    },
    hours() {
      const arr = [];
      for (let h = this.dayStart; h < this.dayEnd; h++) arr.push(h);
      return arr;
    },
    prettyDate() {
      return moment(this.selectedDateStr, "YYYY-MM-DD").format("dddd D MMMM YYYY");
    },
    // The pitch currently chosen in the booking form.
    bookingPitch() {
      return this.pitches.find((p) => String(p.id) === String(this.bookingForm.pitch_id)) || null;
    },
    // Only pitches big enough to split into thirds offer zone selection.
    pitchSplittable() {
      const size = (this.bookingPitch?.pitch_size || "").toLowerCase().replace(/\s/g, "");
      return size === "11-a-side" || size === "9-a-side";
    },
    // Zones already reserved by other bookings that overlap the chosen slot,
    // so the form can grey them out. Returns e.g. ["1","3"].
    bookedZonesForSlot() {
      const f = this.bookingForm;
      if (!f.pitch_id || !f.date || !f.start || !f.end) return [];
      const start = moment.utc(`${f.date} ${f.start}`, "YYYY-MM-DD HH:mm");
      const end = moment.utc(`${f.date} ${f.end}`, "YYYY-MM-DD HH:mm");
      if (!end.isAfter(start)) return [];
      const taken = new Set();
      this.bookings.forEach((b) => {
        if (String(b.pitch_id) !== String(f.pitch_id)) return;
        if (f.id && String(b.id) === String(f.id)) return; // ignore the one being edited
        const bs = moment.utc(b.start_time);
        const be = moment.utc(b.end_time);
        if (bs.isBefore(end) && be.isAfter(start)) {
          this.zonesArray(b.pitch_zone).forEach((z) => taken.add(z));
        }
      });
      return [...taken];
    },
  },
  watch: {
    filterTeamId() {
      /* filtering is reactive via *ForPitch getters */
    },
  },
  async created() {
    const ok = await this.$store.dispatch(CHECK_PROFILE_HAS_DATA);
    if (ok === true && this.getProfile) {
      this.userData = this.getProfile;
      this.init();
    } else {
      const clientId = JSON.parse(localStorage.getItem("the_w_selectedClientId"));
      const res = await Users.find({ clientId, action: "getUserByID" }).catch(() => null);
      if (res && res.data && res.data.length) {
        this.userData = res.data[0];
        this.init();
      }
    }
  },
  methods: {
    emptyBooking(date) {
      return {
        id: null,
        pitch_id: "",
        title: "",
        booking_type: "Training",
        team_id: "",
        pitch_zone: null, // null = full pitch; else "1", "2", "1,2", …
        date: date,
        start: "18:00",
        end: "19:00",
        notes: "",
      };
    },
    // ---- pitch zones (thirds) ----
    // A stored pitch_zone as an array of "1"/"2"/"3"; null/'' = full pitch → [].
    zonesArray(pz) {
      if (pz === null || pz === undefined || pz === "" || pz === "fullPitch") {
        return ["1", "2", "3"];
      }
      return String(pz)
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
    },
    // Selected zones on the form (empty array = full pitch, stored as null).
    formZones() {
      const z = this.bookingForm.pitch_zone;
      if (!z) return [];
      return String(z).split(",").map((s) => s.trim()).filter(Boolean);
    },
    isFullPitch() {
      return !this.bookingForm.pitch_zone;
    },
    isZoneOn(n) {
      return this.formZones().includes(String(n));
    },
    setFullPitch() {
      this.bookingForm.pitch_zone = null;
    },
    toggleZone(n) {
      const k = String(n);
      const set = new Set(this.formZones());
      set.has(k) ? set.delete(k) : set.add(k);
      const arr = [...set].sort();
      // none or all three → full pitch (null), matching the games convention.
      this.bookingForm.pitch_zone = arr.length === 0 || arr.length === 3 ? null : arr.join(",");
    },
    // Human label for a stored pitch_zone (used on blocks).
    zoneLabel(pz) {
      if (!pz || pz === "fullPitch") return "";
      const zones = this.zonesArray(pz);
      return zones.length === 1 ? `Zone ${zones[0]}` : `Zones ${zones.join(" & ")}`;
    },

    async init() {
      await this.loadPitches();
      this.loadTeams();
      this.loadGames();
      this.loadBookings();
    },
    async loadPitches() {
      const res = await Pitches.find({ clubId: this.userData.clubId, action: "getAllPitchesForClub" }).catch(() => null);
      if (res && Array.isArray(res.data)) this.pitches = res.data;
    },
    async loadTeams() {
      const res = await Teams.find({ action: "getTeamsDataById", clubId: this.userData.clubId }).catch(() => null);
      if (res && Array.isArray(res.data)) this.clubTeams = this.removeDuplicates(res.data, "id");
    },
    async loadGames() {
      const res = await Games.find({
        action: "getAllGamesEventsByClubId",
        season_start_date: moment().subtract(1, "months").format("YYYY-MM-DD"),
        season_end_date: moment().add(6, "months").format("YYYY-MM-DD"),
      }).catch(() => null);
      if (res && Array.isArray(res.data)) this.games = res.data;
    },
    async loadBookings() {
      const res = await PitchBookings.find({
        action: "getPitchBookings",
        clubId: this.userData.clubId,
        start_date: moment().subtract(1, "months").format("YYYY-MM-DD"),
        end_date: moment().add(6, "months").format("YYYY-MM-DD"),
      }).catch(() => null);
      if (res && Array.isArray(res.data)) this.bookings = res.data;
    },

    // ---- time / positioning helpers ----
    minsOfDay(t) {
      const m = moment.utc(t);
      return m.hour() * 60 + m.minute();
    },
    onDay(t) {
      return moment.utc(t).format("YYYY-MM-DD") === this.selectedDateStr;
    },
    blockStyle(startMin, endMin, color) {
      const winStart = this.dayStart * 60;
      const winEnd = this.dayEnd * 60;
      const s = Math.max(startMin, winStart);
      const e = Math.min(endMin, winEnd);
      const total = winEnd - winStart;
      const left = ((s - winStart) / total) * 100;
      const width = Math.max(((e - s) / total) * 100, 2);
      return { left: left + "%", width: width + "%", background: color };
    },
    timeLabel(mins) {
      const h = Math.floor(mins / 60);
      const m = mins % 60;
      return moment({ hour: h, minute: m }).format("H:mm");
    },
    formatHour(h) {
      return moment({ hour: h }).format("Ha");
    },

    gamesForPitch(pitchId) {
      return this.games
        .filter((g) => String(g.pitch_id) === String(pitchId) && g.kickOffTime && this.onDay(g.kickOffTime))
        .filter((g) => !this.filterTeamId || String(g.teamId) === this.filterTeamId)
        .map((g) => this.decorateGame(g))
        .filter((g) => g._end > this.dayStart * 60 && g._start < this.dayEnd * 60);
    },
    bookingsForPitch(pitchId) {
      return this.bookings
        .filter((b) => String(b.pitch_id) === String(pitchId) && this.onDay(b.start_time))
        .filter((b) => !this.filterTeamId || String(b.team_id) === this.filterTeamId)
        .map((b) => ({
          ...b,
          _start: this.minsOfDay(b.start_time),
          _end: this.minsOfDay(b.end_time),
          _zone: this.zoneLabel(b.pitch_zone),
        }));
    },
    decorateGame(g) {
      let end = g.endTime;
      if (!end || end === "") end = moment.utc(g.kickOffTime).add(2, "hours").toISOString();
      const colors = { "League game": "#019e53", Training: "#1155cc", "Cup game": "#fdc400" };
      const zone = this.setPitchZoneText ? this.setPitchZoneText(g) : "";
      return {
        ...g,
        _start: this.minsOfDay(g.kickOffTime),
        _end: this.minsOfDay(end),
        _color: colors[g.gameType] || "#495057",
        text: `${g.gameType}${zone ? " – " + zone : ""}`,
      };
    },

    // ---- navigation ----
    changeDay(delta) {
      this.selectedDateStr = moment(this.selectedDateStr, "YYYY-MM-DD").add(delta, "days").format("YYYY-MM-DD");
    },
    goToday() {
      this.selectedDateStr = moment().format("YYYY-MM-DD");
    },

    // ---- create booking from grid click ----
    onTrackClick(evt, pitch) {
      if (!this.canBook) return;
      const track = evt.currentTarget;
      const rect = track.getBoundingClientRect();
      const ratio = Math.min(Math.max((evt.clientX - rect.left) / rect.width, 0), 1);
      const totalMins = (this.dayEnd - this.dayStart) * 60;
      let startMin = this.dayStart * 60 + ratio * totalMins;
      startMin = Math.round(startMin / 30) * 30; // snap to 30 min
      const endMin = Math.min(startMin + 60, this.dayEnd * 60);
      this.openBookingModal("add", null, {
        pitch_id: String(pitch.id),
        start: this.timeLabel(startMin),
        end: this.timeLabel(endMin),
      });
    },

    openBookingModal(mode, booking, prefill) {
      this.bookingError = "";
      if (mode === "edit" && booking) {
        this.bookingForm = {
          id: booking.id,
          pitch_id: String(booking.pitch_id),
          title: booking.title,
          booking_type: booking.booking_type || "Other",
          team_id: booking.team_id ? String(booking.team_id) : "",
          pitch_zone: booking.pitch_zone || null,
          date: moment.utc(booking.start_time).format("YYYY-MM-DD"),
          start: moment.utc(booking.start_time).format("HH:mm"),
          end: moment.utc(booking.end_time).format("HH:mm"),
          notes: booking.notes || "",
        };
      } else {
        this.bookingForm = this.emptyBooking(this.selectedDateStr);
        if (this.pitches.length) this.bookingForm.pitch_id = String(this.pitches[0].id);
        if (prefill) Object.assign(this.bookingForm, prefill);
      }
      this.bookingModal = true;
    },
    async saveBooking() {
      const f = this.bookingForm;
      if (!f.pitch_id || !f.title || !f.date || !f.start || !f.end) {
        this.bookingError = "Please fill in pitch, title, date and times.";
        return;
      }
      const payload = {
        action: f.id ? "editPitchBooking" : "addPitchBooking",
        id: f.id,
        clubId: this.userData.clubId,
        pitch_id: f.pitch_id,
        title: f.title,
        booking_type: f.booking_type,
        team_id: f.team_id,
        pitch_zone: this.pitchSplittable ? f.pitch_zone : null,
        start_time: `${f.date} ${f.start}:00`,
        end_time: `${f.date} ${f.end}:00`,
        notes: f.notes,
        created_by: this.userData.id,
      };
      this.saving = true;
      this.bookingError = "";
      const res = await PitchBookings.store(payload).catch((e) => e.response || { data: {} });
      this.saving = false;
      if (res && res.data && res.data.success) {
        this.$toast?.success(f.id ? "Booking updated" : "Booking created");
        this.bookingModal = false;
        this.loadBookings();
      } else {
        this.bookingError = (res && res.data && res.data.error) || "Could not save the booking.";
      }
    },
    async deleteBooking() {
      if (!this.bookingForm.id) return;
      if (!confirm("Delete this booking?")) return;
      this.saving = true;
      const res = await PitchBookings.store({
        action: "deletePitchBooking",
        id: this.bookingForm.id,
        clubId: this.userData.clubId,
      }).catch(() => null);
      this.saving = false;
      if (res && res.data && res.data.success) {
        this.$toast?.success("Booking deleted");
        this.bookingModal = false;
        this.loadBookings();
      } else {
        this.bookingError = "Could not delete the booking.";
      }
    },

    // ---- pitch CRUD ----
    openPitchModal(mode) {
      this.pitchMode = mode;
      this.editPitchId = "";
      this.pitchForm = { name: "", pitch_size: "", pitchType: "", address: "", postCode: "" };
      this.pitchModal = true;
    },
    loadPitchIntoForm() {
      const p = this.pitches.find((x) => String(x.id) === this.editPitchId);
      if (p) this.pitchForm = { ...p };
    },
    async savePitch() {
      const f = this.pitchForm;
      if (!f.name) {
        this.$toast?.error("Pitch name is required");
        return;
      }
      this.saving = true;
      let res;
      if (this.pitchMode === "add") {
        res = await Pitches.find({
          action: "addPitch",
          clubId: this.userData.clubId,
          name: f.name,
          pitchSize: f.pitch_size,
          pitchType: f.pitchType,
          address: f.address,
          postCode: f.postCode,
        }).catch(() => null);
      } else {
        res = await Pitches.find({
          action: "editPitch",
          id: f.id,
          clubId: this.userData.clubId,
          name: f.name,
          pitch_size: f.pitch_size,
          pitchType: f.pitchType,
          address: f.address,
          postCode: f.postCode,
        }).catch(() => null);
      }
      this.saving = false;
      if (res) {
        this.$toast?.success(this.pitchMode === "add" ? "Pitch added" : "Pitch updated");
        this.pitchModal = false;
        this.loadPitches();
      }
    },

    showGameDetails(g) {
      this.selectedGame = g;
      this.gameModal = true;
    },
  },
};
</script>

<style scoped lang="scss">
$border: #e2e8f0;
$muted: #64748b;

.pitch-scheduler {
  padding: 1rem 0 2rem;
}

.ps-toolbar {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem 1rem;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.75rem;
}
.ps-date-nav {
  display: flex;
  align-items: center;
  gap: 0.4rem;
}
.ps-date-label {
  font-weight: 600;
  color: #1a202c;
  margin-left: 0.4rem;
}
.ps-date-input {
  border: 1px solid $border;
  border-radius: 8px;
  padding: 0.35rem 0.5rem;
}
.ps-toolbar-right {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  align-items: center;
}
.ps-filter {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  label { font-size: 0.8rem; color: $muted; }
  select { border: 1px solid $border; border-radius: 8px; padding: 0.35rem 0.5rem; }
}

.ps-nav-btn,
.ps-today-btn {
  border: 1px solid #cbd5e1;
  background: #fff;
  color: #334155;
  font-weight: 600;
  border-radius: 8px;
  padding: 0.35rem 0.7rem;
  cursor: pointer;
  line-height: 1;
  &:hover { background: #f1f5f9; border-color: #94a3b8; }
  i { font-size: 1rem; }
}
.ps-nav-btn { min-width: 34px; }

.ps-btn {
  border-radius: 8px;
  padding: 0.4rem 0.75rem;
  font-weight: 600;
  font-size: 0.85rem;
  cursor: pointer;
  border: 1px solid transparent;
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
}
.ps-btn-primary { background: #16a34a; color: #fff; &:hover { background: #15803d; } }
.ps-btn-outline { background: #fff; border-color: #cbd5e1; color: #334155; &:hover { background: #f1f5f9; border-color: #94a3b8; } }
.ps-btn-danger { background: #fff; border-color: #fecaca; color: #dc2626; &:hover { background: #fef2f2; } }
.ps-btn:disabled { opacity: 0.6; cursor: default; }

.ps-legend {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem 1rem;
  font-size: 0.75rem;
  color: $muted;
  margin-bottom: 0.5rem;
}
.ps-legend-item { display: inline-flex; align-items: center; gap: 0.3rem; }
.ps-swatch { width: 12px; height: 12px; border-radius: 3px; display: inline-block; }
.ps-swatch-fixture {
  width: 12px; height: 12px; border-radius: 3px; display: inline-block;
  background: repeating-linear-gradient(45deg, #94a3b8, #94a3b8 3px, #cbd5e1 3px, #cbd5e1 6px);
}

.ps-empty {
  text-align: center;
  padding: 3rem 1rem;
  color: $muted;
  i { font-size: 2.5rem; }
  p { margin: 0.5rem 0 1rem; }
}

.ps-grid-scroll { overflow-x: auto; border: 1px solid $border; border-radius: 12px; }
.ps-grid { }
.ps-grid-head { display: flex; background: #f8fafc; border-bottom: 1px solid $border; position: sticky; top: 0; }
.ps-corner {
  flex-shrink: 0;
  padding: 0.5rem 0.75rem;
  font-weight: 600;
  font-size: 0.8rem;
  color: $muted;
  border-right: 1px solid $border;
}
.ps-hours { display: flex; }
.ps-hour-label {
  flex-shrink: 0;
  text-align: center;
  font-size: 0.7rem;
  color: $muted;
  padding: 0.5rem 0;
  border-right: 1px solid $border;
}

.ps-row { display: flex; border-bottom: 1px solid $border; &:last-child { border-bottom: none; } }
.ps-row-name {
  flex-shrink: 0;
  padding: 0.5rem 0.75rem;
  border-right: 1px solid $border;
  display: flex;
  flex-direction: column;
  justify-content: center;
  background: #fff;
}
.ps-pitch-name { font-weight: 600; font-size: 0.85rem; color: #1a202c; }
.ps-pitch-sub { font-size: 0.7rem; color: $muted; }

.ps-track {
  position: relative;
  flex-shrink: 0;
  height: 64px;
  display: flex;
  cursor: copy;
}
.ps-cell { flex-shrink: 0; height: 100%; border-right: 1px solid #f1f5f9; }

.ps-block {
  position: absolute;
  top: 6px;
  bottom: 6px;
  border-radius: 6px;
  color: #fff;
  padding: 3px 6px;
  overflow: hidden;
  cursor: pointer;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
  justify-content: center;
  line-height: 1.1;
  &:hover { filter: brightness(1.05); }
}
.ps-block-time { font-size: 0.62rem; opacity: 0.9; white-space: nowrap; }
.ps-block-title { font-size: 0.72rem; font-weight: 600; white-space: nowrap; text-overflow: ellipsis; overflow: hidden; }
.ps-block-fixture { border: 2px dashed rgba(255, 255, 255, 0.7); cursor: pointer; }

.ps-hint { font-size: 0.75rem; color: $muted; margin-top: 0.6rem; }

/* Modals */
.ps-modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1080;
  padding: 1rem;
}
.ps-modal {
  background: #fff;
  border-radius: 14px;
  width: 100%;
  max-width: 520px;
  max-height: 90vh;
  overflow: auto;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.2);
}
.ps-modal-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.25rem;
  border-bottom: 1px solid $border;
  h3 { margin: 0; font-size: 1.05rem; }
}
.ps-close { border: none; background: none; font-size: 1.5rem; line-height: 1; cursor: pointer; color: $muted; }
.ps-modal-body { padding: 1.25rem; }
.ps-modal-foot {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 1rem 1.25rem;
  border-top: 1px solid $border;
}
.ps-spacer { flex: 1; }

.ps-field { margin-bottom: 0.85rem; display: flex; flex-direction: column; }
.ps-field label { font-size: 0.78rem; color: $muted; margin-bottom: 0.25rem; }
.ps-field input,
.ps-field select,
.ps-field textarea {
  border: 1px solid $border;
  border-radius: 8px;
  padding: 0.5rem 0.6rem;
  font-size: 0.9rem;
  width: 100%;
}
.ps-field-row { display: flex; gap: 0.75rem; .ps-field { flex: 1; } }

.ps-zones {
  background: #f4fff5;
  border: 1px solid #d6eddb;
  border-radius: 10px;
  padding: 0.75rem;
}
.ps-zones-hint { font-size: 0.72rem; color: $muted; margin: 0 0 0.5rem; }
.ps-zone-opt {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.85rem;
  margin-bottom: 0.5rem;
  input { width: auto; }
}
.ps-zone-grid { display: flex; gap: 0.5rem; }
.ps-zone-cell {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.15rem;
  border: 1px dashed #93a2bb;
  border-radius: 8px;
  padding: 0.6rem 0.3rem;
  font-size: 0.8rem;
  cursor: pointer;
  background: #fff;
  input { width: auto; }
  small { color: #b03030; font-size: 0.65rem; }
  &.on { background: rgba(198, 234, 202, 0.6); border-style: solid; border-color: #16a34a; }
  &.taken { opacity: 0.55; cursor: not-allowed; background: #f6f7f8; border-color: #cbd5e1; }
}
.ps-error { color: #dc2626; font-size: 0.8rem; margin: 0.25rem 0 0; }
.ps-muted { color: $muted; font-size: 0.78rem; margin-top: 0.75rem; }

@media (max-width: 640px) {
  .ps-toolbar { flex-direction: column; align-items: stretch; }
  .ps-date-label { display: none; }
}
</style>
